/**
 * Tickets/Support Page Component
 * Dashboard, ticket list, ticket detail, and canned responses
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../store/AdminContext';
import { useAuth } from '../store/AuthContext';
import { apiService } from '../services/apiService';
import './TicketsPage.css';

interface Ticket {
  id: string;
  userId: string;
  subject: string;
  status: string;
  priority: string;
  assignedAdminId: string | null;
  createdAt: string;
  updatedAt: string;
  userEmail?: string;
  username?: string;
}

interface TicketMessage {
  id: string;
  ticketId: string;
  authorType: string;
  authorId: string;
  body: string;
  createdAt: string;
}

interface CannedResponse {
  id: string;
  name: string;
  content: string;
  category: string | null;
  createdAt: string;
}

interface TicketsPageProps {
  view: string;
}

const TicketsPage: React.FC<TicketsPageProps> = ({ view }) => {
  const { setCurrentPage } = useAdmin();
  const { admin } = useAuth();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [total, setTotal] = useState(0);
  const [cannedResponses, setCannedResponses] = useState<CannedResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [selectedTicket, setSelectedTicket] = useState<{ ticket: Ticket; messages: TicketMessage[] } | null>(null);
  const [replyBody, setReplyBody] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [cannedModalOpen, setCannedModalOpen] = useState(false);
  const [editingCanned, setEditingCanned] = useState<CannedResponse | null>(null);
  const [cannedName, setCannedName] = useState('');
  const [cannedContent, setCannedContent] = useState('');
  const [cannedCategory, setCannedCategory] = useState('');

  const [openCount, setOpenCount] = useState(0);
  const [escalatedCount, setEscalatedCount] = useState(0);

  const [lookupUserId, setLookupUserId] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupError, setLookupError] = useState<string | null>(null);
  const [lookupResult, setLookupResult] = useState<Awaited<ReturnType<typeof apiService.getReferralDiagnostics>> | null>(null);
  const [lookupSearchQ, setLookupSearchQ] = useState('');
  const [lookupSearchLoading, setLookupSearchLoading] = useState(false);
  const [lookupSearchResults, setLookupSearchResults] = useState<Array<{ id: string; email: string; username: string }>>([]);
  const [lookupSearchError, setLookupSearchError] = useState<string | null>(null);

  const loadTickets = useCallback(async (filters?: { status?: 'open' | 'in_progress' | 'escalated' | 'resolved' | 'closed'; assignedAdminId?: string; escalated?: boolean }) => {
    try {
      setLoading(true);
      setError(null);
      const { tickets: data, total: t } = await apiService.getTickets({
        ...filters,
        limit: 50,
        offset: 0,
      });
      setTickets(data || []);
      setTotal(t ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tickets');
      setTickets([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [allRes, openRes, escalatedRes] = await Promise.all([
        apiService.getTickets({ limit: 10, offset: 0 }),
        apiService.getTickets({ status: 'open', limit: 1, offset: 0 }),
        apiService.getTickets({ escalated: true, limit: 1, offset: 0 }),
      ]);
      setTickets(allRes.tickets || []);
      setTotal(allRes.total ?? 0);
      setOpenCount(openRes.total ?? 0);
      setEscalatedCount(escalatedRes.total ?? 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load dashboard');
      setTickets([]);
      setTotal(0);
      setOpenCount(0);
      setEscalatedCount(0);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCannedResponses = useCallback(async () => {
    try {
      const { responses } = await apiService.getCannedResponses();
      setCannedResponses(responses || []);
    } catch (err) {
      console.error('Failed to load canned responses', err);
      setCannedResponses([]);
    }
  }, []);

  useEffect(() => {
    if (view === 'support-dashboard') {
      loadDashboardData();
    } else if (view === 'tickets-all') {
      loadTickets(statusFilter ? { status: statusFilter as 'open' | 'in_progress' | 'escalated' | 'resolved' | 'closed' } : undefined);
    } else if (view === 'tickets-mine' && admin?.id) {
      loadTickets({ assignedAdminId: admin.id });
    } else if (view === 'tickets-escalated') {
      loadTickets({ escalated: true });
    } else if (view === 'canned-responses') {
      setLoading(true);
      loadCannedResponses().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [view, statusFilter, admin?.id, loadTickets, loadDashboardData, loadCannedResponses]);

  const loadTicketDetail = async (id: string) => {
    try {
      const result = await apiService.getTicket(id);
      if (result) setSelectedTicket(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load ticket');
    }
  };

  const handleReply = async () => {
    if (!selectedTicket || !replyBody.trim()) return;
    setSendingReply(true);
    try {
      await apiService.addTicketMessage(selectedTicket.ticket.id, replyBody.trim());
      setReplyBody('');
      const result = await apiService.getTicket(selectedTicket.ticket.id);
      if (result) setSelectedTicket(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const reloadTicketsForView = () => {
    if (view === 'tickets-all') {
      loadTickets(statusFilter ? { status: statusFilter as 'open' | 'in_progress' | 'escalated' | 'resolved' | 'closed' } : undefined);
    } else if (view === 'tickets-mine' && admin?.id) {
      loadTickets({ assignedAdminId: admin.id });
    } else if (view === 'tickets-escalated') {
      loadTickets({ escalated: true });
    }
  };

  const handleUpdateStatus = async (ticketId: string, status: string) => {
    try {
      await apiService.updateTicket(ticketId, { status });
      if (selectedTicket?.ticket.id === ticketId) {
        setSelectedTicket({ ...selectedTicket, ticket: { ...selectedTicket.ticket, status } });
      }
      reloadTicketsForView();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update status');
    }
  };

  const openCannedModal = (item?: CannedResponse) => {
    setEditingCanned(item || null);
    setCannedName(item?.name || '');
    setCannedContent(item?.content || '');
    setCannedCategory(item?.category || '');
    setCannedModalOpen(true);
  };

  const saveCanned = async () => {
    try {
      if (editingCanned) {
        await apiService.updateCannedResponse(editingCanned.id, { name: cannedName, content: cannedContent, category: cannedCategory || undefined });
      } else {
        await apiService.createCannedResponse({ name: cannedName, content: cannedContent, category: cannedCategory || undefined });
      }
      setCannedModalOpen(false);
      loadCannedResponses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    }
  };

  const deleteCanned = async (id: string) => {
    if (!window.confirm('Delete this canned response?')) return;
    try {
      await apiService.deleteCannedResponse(id);
      loadCannedResponses();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const formatDate = (d: string) => (d ? new Date(d).toLocaleString() : '—');

  const getStatusClass = (s: string) => {
    if (s === 'open') return 'open';
    if (s === 'escalated') return 'escalated';
    if (s === 'resolved' || s === 'closed') return 'resolved';
    return 'in_progress';
  };

  // --- Support Dashboard ---
  if (view === 'support-dashboard') {
    return (
      <div className="tickets-page">
        <div className="page-head">
          <div>
            <h1 className="page-title">Support Dashboard</h1>
            <p className="page-sub">Ticket overview and quick actions</p>
          </div>
        </div>

        <div className="tickets-stats">
          <div className="ticket-stat clickable" onClick={() => setCurrentPage('tickets-all')}>
            <div className="ticket-stat-icon orange">🎫</div>
            <div className="ticket-stat-val">{total}</div>
            <div className="ticket-stat-label">Total Tickets</div>
          </div>
          <div className="ticket-stat clickable" onClick={() => setCurrentPage('tickets-all')}>
            <div className="ticket-stat-icon cyan">📂</div>
            <div className="ticket-stat-val">{openCount}</div>
            <div className="ticket-stat-label">Open</div>
          </div>
          <div className="ticket-stat clickable" onClick={() => setCurrentPage('tickets-escalated')}>
            <div className="ticket-stat-icon red">⚠️</div>
            <div className="ticket-stat-val">{escalatedCount}</div>
            <div className="ticket-stat-label">Escalated</div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <span className="card-title">Recent Tickets</span>
            <button className="btn btn-secondary" onClick={() => setCurrentPage('tickets-all')}>
              View All
            </button>
          </div>
          <div className="card-body">
            {loading ? (
              <div className="tickets-loading">Loading...</div>
            ) : tickets.length === 0 ? (
              <div className="tickets-empty">No tickets yet</div>
            ) : (
              <div className="tickets-list">
                {tickets.slice(0, 10).map((t) => (
                  <div
                    key={t.id}
                    className="ticket-row"
                    onClick={() => {
                      setSelectedTicket(null);
                      loadTicketDetail(t.id);
                    }}
                  >
                    <div className="ticket-row-subject">{t.subject}</div>
                    <div className="ticket-row-meta">
                      {t.username || t.userEmail || t.userId} · {formatDate(t.createdAt)}
                    </div>
                    <span className={`status-badge status-${getStatusClass(t.status)}`}>{t.status}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {selectedTicket && (
          <div className="modal-overlay" onClick={() => setSelectedTicket(null)}>
            <div className="modal-content ticket-detail-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{selectedTicket.ticket.subject}</h2>
                <button className="modal-close" onClick={() => setSelectedTicket(null)}>×</button>
              </div>
              <div className="modal-body">
                <div className="ticket-detail-meta">
                  {selectedTicket.ticket.username || selectedTicket.ticket.userEmail} · {selectedTicket.ticket.priority} · {selectedTicket.ticket.status}
                </div>
                <div className="ticket-messages">
                  {selectedTicket.messages.map((m) => (
                    <div key={m.id} className={`ticket-msg ${m.authorType}`}>
                      <div className="ticket-msg-body">{m.body}</div>
                      <div className="ticket-msg-meta">{m.authorType} · {formatDate(m.createdAt)}</div>
                    </div>
                  ))}
                </div>
                <div className="ticket-reply">
                  <textarea
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    placeholder="Type your reply..."
                    rows={3}
                  />
                  <button className="btn btn-primary" onClick={handleReply} disabled={sendingReply || !replyBody.trim()}>
                    {sendingReply ? 'Sending...' : 'Send Reply'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- All Tickets / My Tickets / Escalated ---
  if (view === 'tickets-all' || view === 'tickets-mine' || view === 'tickets-escalated') {
    const viewTitle = view === 'tickets-all' ? 'All Tickets' : view === 'tickets-mine' ? 'My Tickets' : 'Escalated Tickets';
    return (
      <div className="tickets-page">
        <div className="page-head">
          <div>
            <h1 className="page-title">{viewTitle}</h1>
            <p className="page-sub">
              {view === 'tickets-all' && 'View and manage all support tickets'}
              {view === 'tickets-mine' && 'Tickets assigned to you'}
              {view === 'tickets-escalated' && 'Escalated tickets requiring attention'}
            </p>
          </div>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 16 }}>
            {error}
          </div>
        )}

        {view === 'tickets-all' && (
          <div className="filter-row">
            <label>Status:</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All</option>
              <option value="open">Open</option>
              <option value="in_progress">In Progress</option>
              <option value="escalated">Escalated</option>
              <option value="resolved">Resolved</option>
              <option value="closed">Closed</option>
            </select>
          </div>
        )}

        <div className="card">
          <div className="card-body">
            {loading ? (
              <div className="tickets-loading">Loading...</div>
            ) : tickets.length === 0 ? (
              <div className="tickets-empty">No tickets found</div>
            ) : (
              <div className="table-wrap">
                <table className="tickets-table">
                  <thead>
                    <tr>
                      <th>Subject</th>
                      <th>User</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Updated</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((t) => (
                      <tr key={t.id}>
                        <td className="ticket-subject">{t.subject}</td>
                        <td>{t.username || t.userEmail || t.userId}</td>
                        <td><span className={`status-badge status-${getStatusClass(t.status)}`}>{t.status}</span></td>
                        <td>{t.priority}</td>
                        <td>{formatDate(t.updatedAt)}</td>
                        <td>
                          <button className="btn btn-sm btn-primary" onClick={() => loadTicketDetail(t.id)}>View</button>
                          {t.status !== 'resolved' && t.status !== 'closed' && (
                            <select
                              className="status-select"
                              value={t.status}
                              onChange={(e) => handleUpdateStatus(t.id, e.target.value)}
                            >
                              <option value="open">Open</option>
                              <option value="in_progress">In Progress</option>
                              <option value="escalated">Escalated</option>
                              <option value="resolved">Resolved</option>
                              <option value="closed">Closed</option>
                            </select>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {selectedTicket && (
          <div className="modal-overlay" onClick={() => setSelectedTicket(null)}>
            <div className="modal-content ticket-detail-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{selectedTicket.ticket.subject}</h2>
                <button className="modal-close" onClick={() => setSelectedTicket(null)}>×</button>
              </div>
              <div className="modal-body">
                <div className="ticket-detail-meta">
                  {selectedTicket.ticket.username || selectedTicket.ticket.userEmail} · {selectedTicket.ticket.priority} · {selectedTicket.ticket.status}
                </div>
                <div className="ticket-messages">
                  {selectedTicket.messages.map((m) => (
                    <div key={m.id} className={`ticket-msg ${m.authorType}`}>
                      <div className="ticket-msg-body">{m.body}</div>
                      <div className="ticket-msg-meta">{m.authorType} · {formatDate(m.createdAt)}</div>
                    </div>
                  ))}
                </div>
                <div className="ticket-reply">
                  <textarea
                    value={replyBody}
                    onChange={(e) => setReplyBody(e.target.value)}
                    placeholder="Type your reply..."
                    rows={3}
                  />
                  <button className="btn btn-primary" onClick={handleReply} disabled={sendingReply || !replyBody.trim()}>
                    {sendingReply ? 'Sending...' : 'Send Reply'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- Canned Responses ---
  if (view === 'canned-responses') {
    return (
      <div className="tickets-page">
        <div className="page-head">
          <div>
            <h1 className="page-title">Canned Responses</h1>
            <p className="page-sub">Pre-defined response templates for support tickets</p>
          </div>
          <button className="btn btn-primary" onClick={() => openCannedModal()}>
            + Add Template
          </button>
        </div>

        {error && (
          <div className="alert alert-error" style={{ marginBottom: 16 }}>
            {error}
          </div>
        )}

        <div className="card">
          <div className="card-body">
            {loading ? (
              <div className="tickets-loading">Loading...</div>
            ) : cannedResponses.length === 0 ? (
              <div className="tickets-empty">No canned responses. Create one to get started.</div>
            ) : (
              <div className="canned-list">
                {cannedResponses.map((r) => (
                  <div key={r.id} className="canned-item">
                    <div className="canned-item-main">
                      <div className="canned-item-name">{r.name}</div>
                      {r.category && <span className="canned-item-cat">{r.category}</span>}
                      <div className="canned-item-content">{r.content}</div>
                    </div>
                    <div className="canned-item-actions">
                      <button className="btn btn-sm btn-secondary" onClick={() => openCannedModal(r)}>Edit</button>
                      <button className="btn btn-sm btn-danger" onClick={() => deleteCanned(r.id)}>Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {cannedModalOpen && (
          <div className="modal-overlay" onClick={() => setCannedModalOpen(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>{editingCanned ? 'Edit Template' : 'New Template'}</h2>
                <button className="modal-close" onClick={() => setCannedModalOpen(false)}>×</button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" value={cannedName} onChange={(e) => setCannedName(e.target.value)} placeholder="e.g. Welcome message" />
                </div>
                <div className="form-group">
                  <label>Category (optional)</label>
                  <input type="text" value={cannedCategory} onChange={(e) => setCannedCategory(e.target.value)} placeholder="e.g. onboarding" />
                </div>
                <div className="form-group">
                  <label>Content</label>
                  <textarea value={cannedContent} onChange={(e) => setCannedContent(e.target.value)} rows={6} placeholder="Template content..." />
                </div>
              </div>
              <div className="modal-actions">
                <button className="btn btn-secondary" onClick={() => setCannedModalOpen(false)}>Cancel</button>
                <button className="btn btn-primary" onClick={saveCanned} disabled={!cannedName.trim() || !cannedContent.trim()}>
                  Save
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const runUserSearch = async () => {
    const q = lookupSearchQ.trim();
    if (q.length < 2) {
      setLookupSearchError('Enter at least 2 characters (email or username).');
      setLookupSearchResults([]);
      return;
    }
    setLookupSearchLoading(true);
    setLookupSearchError(null);
    setLookupSearchResults([]);
    try {
      const { users } = await apiService.searchAdminUsers(q, 20);
      setLookupSearchResults(users || []);
      if (!users?.length) {
        setLookupSearchError('No users matched.');
      }
    } catch (err) {
      setLookupSearchError(err instanceof Error ? err.message : 'Search failed');
    } finally {
      setLookupSearchLoading(false);
    }
  };

  const runReferralDiagnostic = async () => {
    const id = lookupUserId.trim();
    if (!id) {
      setLookupError('Enter the referred user UUID (the invitee / referee).');
      return;
    }
    setLookupLoading(true);
    setLookupError(null);
    setLookupResult(null);
    try {
      const data = await apiService.getReferralDiagnostics(id);
      setLookupResult(data);
    } catch (err) {
      setLookupError(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setLookupLoading(false);
    }
  };

  if (view === 'user-lookup') {
    const fmt = (cents: number) => `£${(cents / 100).toFixed(2)}`;
    const res = lookupResult?.resolution;
    return (
      <div className="tickets-page">
        <div className="page-head">
          <div>
            <h1 className="page-title">User Lookup</h1>
            <p className="page-sub">Share &amp; Earn payout diagnostic for a referred user</p>
          </div>
        </div>

        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-body">
            <p style={{ marginTop: 0, color: 'var(--text-muted)', fontSize: 14 }}>
              Enter the <strong>referred user&apos;s</strong> user ID (UUID), not the referrer. This matches{' '}
              <code style={{ fontSize: 13 }}>GET /api/v1/admin/referrals/:userId/diagnostics</code>.
            </p>
            <div className="form-group" style={{ maxWidth: 560 }}>
              <label htmlFor="lookup-search">Find user (email or username)</label>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  id="lookup-search"
                  type="search"
                  value={lookupSearchQ}
                  onChange={(e) => setLookupSearchQ(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      void runUserSearch();
                    }
                  }}
                  placeholder="Partial email or username"
                  autoComplete="off"
                  style={{ flex: '1 1 220px', minWidth: 200 }}
                />
                <button type="button" className="btn btn-secondary" disabled={lookupSearchLoading} onClick={() => void runUserSearch()}>
                  {lookupSearchLoading ? 'Searching…' : 'Search'}
                </button>
              </div>
              {lookupSearchError && (
                <p style={{ marginTop: 8, marginBottom: 0, fontSize: 13, color: 'var(--danger, #c0392b)' }}>{lookupSearchError}</p>
              )}
              {lookupSearchResults.length > 0 && (
                <ul
                  style={{
                    marginTop: 12,
                    marginBottom: 0,
                    paddingLeft: 0,
                    listStyle: 'none',
                    maxHeight: 220,
                    overflowY: 'auto',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 8,
                  }}
                >
                  {lookupSearchResults.map((u) => (
                    <li
                      key={u.id}
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        gap: 8,
                        padding: '10px 12px',
                        borderBottom: '1px solid var(--glass-border)',
                      }}
                    >
                      <span style={{ fontSize: 13 }}>
                        <strong>{u.email}</strong>
                        {u.username ? <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>({u.username})</span> : null}
                      </span>
                      <button type="button" className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={() => setLookupUserId(u.id)}>
                        Use this UUID
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="form-group" style={{ maxWidth: 520 }}>
              <label htmlFor="lookup-uuid">Referred user UUID</label>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                <input
                  id="lookup-uuid"
                  type="text"
                  value={lookupUserId}
                  onChange={(e) => setLookupUserId(e.target.value)}
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  autoComplete="off"
                  style={{ flex: '1 1 280px', minWidth: 200 }}
                />
                <button type="button" className="btn btn-primary" disabled={lookupLoading} onClick={runReferralDiagnostic}>
                  {lookupLoading ? 'Loading…' : 'Run diagnostic'}
                </button>
              </div>
            </div>
            {lookupError && (
              <div className="alert alert-error" style={{ marginTop: 12 }}>
                {lookupError}
              </div>
            )}
          </div>
        </div>

        {lookupResult && (
          <div className="card">
            <div className="card-body">
              <h2 style={{ marginTop: 0, fontSize: 18 }}>Result</h2>
              <p
                style={{
                  fontSize: 15,
                  lineHeight: 1.5,
                  padding: '12px 14px',
                  borderRadius: 8,
                  background: 'var(--input)',
                  border: '1px solid var(--glass-border)',
                }}
              >
                {lookupResult.summary}
              </p>

              <div style={{ marginTop: 16 }}>
                <strong>Eligibility</strong>{' '}
                <span className={lookupResult.eligibilityCheck.eligible ? 'status status-approved' : 'status status-rejected'}>
                  {lookupResult.eligibilityCheck.eligible ? 'Eligible' : 'Not eligible'}
                </span>
                {lookupResult.eligibilityCheck.reason && (
                  <span style={{ color: 'var(--text-muted)', marginLeft: 8 }}>{lookupResult.eligibilityCheck.reason}</span>
                )}
              </div>

              {lookupResult.hasAttribution && (
                <dl className="referral-diag-dl">
                  <dt>Settled bets (user)</dt>
                  <dd>{lookupResult.settledBetsCount ?? 0}</dd>
                  <dt>Paid payout index count</dt>
                  <dd>{lookupResult.paidSettledBetCount ?? 0}</dd>
                  <dt>Trigger completed</dt>
                  <dd>{lookupResult.triggerOccurred ? 'Yes' : 'No'}</dd>
                  <dt>Review status</dt>
                  <dd>{lookupResult.reviewStatus ?? '—'}</dd>
                  <dt>Referrer</dt>
                  <dd>
                    {lookupResult.referrerEmail ?? '—'}{' '}
                    {lookupResult.referrerId && (
                      <code style={{ fontSize: 12 }}>{lookupResult.referrerId}</code>
                    )}
                  </dd>
                  <dt>Attribution ID</dt>
                  <dd>
                    <code style={{ fontSize: 12 }}>{lookupResult.attributionId ?? '—'}</code>
                  </dd>
                </dl>
              )}

              {res && (
                <>
                  <h3 style={{ marginTop: 24, fontSize: 16 }}>Resolved campaign terms</h3>
                  <dl className="referral-diag-dl">
                    <dt>Policy mode</dt>
                    <dd>{res.policyResolution}</dd>
                    <dt>Campaign disabled</dt>
                    <dd>{res.campaignDisabled ? 'Yes' : 'No'}</dd>
                    <dt>Click country / platform</dt>
                    <dd>
                      {res.refereeCountry ?? '—'} / {res.refereePlatform ?? '—'}
                    </dd>
                    <dt>Referrer brand / cohort tags</dt>
                    <dd>
                      {res.referrerBrandTag ?? '—'} / {res.referrerCohortTag ?? '—'}
                    </dd>
                    <dt>Fixed amounts (referrer / referee)</dt>
                    <dd>
                      {fmt(res.referrerCents)} / {fmt(res.refereeCents)}
                    </dd>
                    <dt>Payout trigger</dt>
                    <dd>{res.payoutTrigger}</dd>
                    <dt>Max bets / basis</dt>
                    <dd>
                      {res.maxBetsCount} / {res.basis}
                      {res.basis === 'platform_fee' && ` (${res.sharePercent}% of platform fee)`}
                    </dd>
                  </dl>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // --- Live Chat (placeholder) ---
  const placeholderViews: Record<string, string> = {
    'live-chat': 'Live Chat',
  };
  const placeholder = placeholderViews[view];
  if (placeholder) {
    return (
      <div className="tickets-page">
        <div className="page-head">
          <div>
            <h1 className="page-title">{placeholder}</h1>
            <p className="page-sub">Coming soon</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="tickets-placeholder">
              <div className="placeholder-icon">💬</div>
              <p>Real-time chat with users</p>
              <p className="placeholder-hint">This feature is coming soon.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tickets-page">
      <div className="page-head">
        <h1 className="page-title">Support</h1>
        <p className="page-sub">Support management</p>
      </div>
      <div className="card">
        <div className="card-body">
          <p style={{ color: 'var(--text-muted)' }}>Unknown view: {view}</p>
        </div>
      </div>
    </div>
  );
};

export default TicketsPage;
